import DOMPurify from "dompurify";

const tags = ["p", "div", "br", "strong", "b", "em", "i", "u", "s", "h1", "h2", "h3", "h4", "h5", "h6", "ol", "ul", "li", "a", "blockquote", "pre", "code", "span", "sub", "sup"];
export const isRichText = (value = "") => /<\/?(?:p|div|br|strong|b|em|i|u|s|h[1-6]|ol|ul|li|a|blockquote|pre|code|span|sub|sup)(?:\s|\/?>)/i.test(value);
export const escapeText = (value = "") => String(value).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");

export function sanitizeRichText(value = "") {
  // Parse only sanitized markup. Translate common Word inline styles into
  // semantic tags before removing all styles, classes and foreign attributes.
  const fragment = DOMPurify.sanitize(String(value), {
    ALLOWED_TAGS: tags, ALLOWED_ATTR: ["href", "title", "style", "start"],
    ALLOW_DATA_ATTR: false, ALLOW_ARIA_ATTR: false, RETURN_DOM_FRAGMENT: true,
  });
  fragment.querySelectorAll("[style]").forEach((element) => {
    const wrappers = [];
    if (/bold|[6-9]00/.test(element.style.fontWeight)) wrappers.push("strong");
    if (element.style.fontStyle === "italic") wrappers.push("em");
    if (/underline/.test(element.style.textDecoration)) wrappers.push("u");
    wrappers.forEach((tag) => {
      const wrapper = document.createElement(tag);
      while (element.firstChild) wrapper.appendChild(element.firstChild);
      element.appendChild(wrapper);
    });
    element.removeAttribute("style");
  });
  const container = document.createElement("div");
  container.appendChild(fragment);
  return DOMPurify.sanitize(container.innerHTML, {
    ALLOWED_TAGS: tags, ALLOWED_ATTR: ["href", "title", "start"],
    ALLOW_DATA_ATTR: false, ALLOW_ARIA_ATTR: false,
  });
}

export const toRichHtml = (value = "") => isRichText(value)
  ? sanitizeRichText(value) : value ? `<div>${escapeText(value).replace(/\r?\n/g, "<br>")}</div>` : "";

// Clipboard fragments often include empty paragraphs and newlines around the
// selection. Trim only the two outside edges, preserving spacing within it.
export function normalizePastedHtml(html) {
  const container = document.createElement("div");
  container.innerHTML = sanitizeRichText(html);
  const trimEdge = (parent, start) => {
    let node;
    while ((node = start ? parent.firstChild : parent.lastChild)) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = node.textContent.replace(start ? /^\s*[\r\n]+\s*/ : /\s*[\r\n]+\s*$/, "");
        if (node.textContent.trim()) break;
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        if (node.tagName === "PRE" || node.tagName === "CODE") break;
        trimEdge(node, start);
        if (node.textContent.trim()) break;
      }
      node.remove();
    }
  };
  trimEdge(container, true);
  trimEdge(container, false);
  return container.innerHTML;
}

// Interpret emphasis only in text nodes, never in HTML attributes, code,
// URLs or mention tokens. The result still passes through the sanitizer.
export function withMarkdownEmphasis(html) {
  const container = document.createElement("div");
  container.innerHTML = sanitizeRichText(html);
  const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  nodes.forEach((node) => {
    if (node.parentElement.closest("pre, code, a")) return;
    const parts = node.textContent.split(/(@\[[^\]]+\]\(\d+\)|https?:\/\/\S+|`[^`\n]*`|\\\*)/g);
    const formatted = parts.map((part, index) => {
      const safe = escapeText(part);
      if (index % 2) return safe;
      return safe
        .replace(/\*\*\*([^*\n]+)\*\*\*/g, "<strong><em>$1</em></strong>")
        .replace(/\*\*([^*\n]+)\*\*/g, "<strong>$1</strong>")
        .replace(/\*([^*\s](?:[^*\n]*[^*\s])?)\*/g, "<em>$1</em>");
    }).join("");
    const template = document.createElement("template");
    template.innerHTML = formatted;
    node.replaceWith(template.content);
  });
  return sanitizeRichText(container.innerHTML);
}
export function richTextToPlain(value = "") {
  if (!isRichText(value)) return String(value);
  const container = document.createElement("div");
  container.innerHTML = sanitizeRichText(value).replace(/<br\s*\/?\s*>|<\/(?:p|div|li|h[1-6])>/gi, "$&\n");
  return container.textContent || "";
}
